import { Wrench } from 'lucide-react'
import { useEffect, useRef, type PointerEvent } from 'react'
import { useDevToolsStore } from 'shared/store/devtools'
import { readCssPx } from '../lib/safe-area'

const ICON_SIZE = 44
const EDGE = 4
/** Pointer travel below this counts as a tap, not a drag. */
const TAP_SLOP_PX = 8

/** Keeps the button inside Telegram's safe area (clear of the fullscreen
 *  controls at the top and the tabbar / home indicator at the bottom). */
function clamp(x: number, y: number) {
	const safeTop = readCssPx('--header-safe-top') - 50
	const safeBottom = readCssPx('--tabbar-safe-bottom')
	return {
		x: Math.min(Math.max(x, EDGE), window.innerWidth - ICON_SIZE - EDGE),
		y: Math.min(
			Math.max(y, safeTop + EDGE),
			window.innerHeight - ICON_SIZE - safeBottom - EDGE,
		),
	}
}

/** Draggable floating dev-tools button; a tap opens the bottom sheet. */
export function FloatingIcon() {
	const iconPos = useDevToolsStore((s) => s.iconPos)
	const setIconPos = useDevToolsStore((s) => s.setIconPos)
	const setSheetOpen = useDevToolsStore((s) => s.setSheetOpen)

	// A position persisted before this fix (or from a non-fullscreen session)
	// may sit under Telegram's controls — pull it back into the safe area.
	useEffect(() => {
		const safe = clamp(iconPos.x, iconPos.y)
		if (safe.x !== iconPos.x || safe.y !== iconPos.y) setIconPos(safe)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const drag = useRef<{
		pointerId: number
		startX: number
		startY: number
		baseX: number
		baseY: number
		moved: boolean
	} | null>(null)

	const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
		e.currentTarget.setPointerCapture(e.pointerId)
		drag.current = {
			pointerId: e.pointerId,
			startX: e.clientX,
			startY: e.clientY,
			baseX: iconPos.x,
			baseY: iconPos.y,
			moved: false,
		}
	}

	const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
		const d = drag.current
		if (!d || d.pointerId !== e.pointerId) return
		const dx = e.clientX - d.startX
		const dy = e.clientY - d.startY
		if (Math.hypot(dx, dy) > TAP_SLOP_PX) d.moved = true
		if (d.moved) setIconPos(clamp(d.baseX + dx, d.baseY + dy))
	}

	const onPointerUp = (e: PointerEvent<HTMLButtonElement>) => {
		const d = drag.current
		if (!d || d.pointerId !== e.pointerId) return
		drag.current = null
		if (!d.moved) setSheetOpen(true)
	}

	return (
		<button
			type='button'
			aria-label='Dev tools'
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={() => (drag.current = null)}
			className='fixed z-[9999] flex items-center justify-center rounded-full border border-[var(--tgui--outline,rgba(255,255,255,0.2))] bg-[var(--tgui--secondary_bg_color,#222)] text-[var(--tgui--link_color,rgb(41,144,255))] shadow-lg'
			style={{
				left: iconPos.x,
				top: iconPos.y,
				width: ICON_SIZE,
				height: ICON_SIZE,
				touchAction: 'none',
			}}
		>
			<Wrench className='size-5' />
		</button>
	)
}
